import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Gift, CheckCircle2, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

export default function WaitlistDialog({ open, onOpenChange, plan = "premium" }) {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null); // {email}

  useEffect(() => {
    if (open) {
      setName(user?.name || "");
      setEmail(user?.email || "");
      setDone(null);
    }
  }, [open, user]);

  const submit = async () => {
    if (!email || !/.+@.+\..+/.test(email)) { toast.error("Ingresa un correo válido."); return; }
    setSaving(true);
    try {
      await api.post("/waitlist", { email: email.trim(), name: name.trim(), plan });
      setDone({ email: email.trim() });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo registrar. Intenta de nuevo.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="waitlist-dialog" className="max-w-md">
        {done ? (
          <div className="text-center py-2">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-green-50 grid place-items-center">
              <CheckCircle2 className="h-7 w-7 text-green-600" />
            </div>
            <h3 className="mt-4 font-heading font-extrabold text-2xl text-xuma-dark">¡Estás en la lista! 🎉</h3>
            <p className="mt-2 text-xuma-muted">
              Te avisaremos a <b className="text-xuma-dark">{done.email}</b> en cuanto Premium esté disponible.
            </p>
            <div className="mt-5 rounded-2xl bg-[#FFF3EE] border border-[#F7C9B8] p-4 text-left flex gap-3">
              <Gift className="h-5 w-5 text-xuma-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-xuma-dark text-sm">Plan Fundador · Gratis 1 año</p>
                <p className="text-sm text-xuma-muted mt-0.5">Por unirte temprano, al activarse Premium tendrás el Plan Fundador sin costo durante 12 meses.</p>
              </div>
            </div>
            <Button data-testid="waitlist-close" onClick={() => onOpenChange(false)} className="mt-6 w-full h-11 rounded-full bg-xuma-primary hover:bg-xuma-primaryHover text-white">
              Listo
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-xuma-primary/10 text-xuma-primary text-xs font-medium px-3 py-1 mb-1">
                <Sparkles className="h-3.5 w-3.5" /> Acceso anticipado
              </span>
              <DialogTitle className="font-heading text-2xl">Únete a la lista de espera</DialogTitle>
              <DialogDescription>
                Premium está por llegar. Déjanos tu correo y serás de los primeros en activarlo —
                <b className="text-xuma-dark"> con Plan Fundador gratis por 1 año</b>.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-1">
              <div>
                <Label className="mb-1 block">Nombre</Label>
                <Input data-testid="waitlist-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" />
              </div>
              <div>
                <Label className="mb-1 block">Correo</Label>
                <Input data-testid="waitlist-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.com" />
              </div>
            </div>
            <div className="rounded-xl bg-[#FFF3EE] border border-[#F7C9B8] p-3 flex items-center gap-2.5 mt-1">
              <Gift className="h-4 w-4 text-xuma-primary shrink-0" />
              <p className="text-xs text-xuma-dark">Los primeros en unirse reciben el <b>Plan Fundador gratis durante 1 año</b>.</p>
            </div>
            <Button data-testid="waitlist-submit" onClick={submit} disabled={saving} className="mt-3 w-full h-11 rounded-full bg-xuma-primary hover:bg-xuma-primaryHover text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apúntame a la lista"}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
